<template>
  <div class="form-container card shadow-sm p-3">
    <div v-if="contasMensagem" class="alert alert-warning" role="alert">{{ contasMensagem }}</div>

    <div class="header d-flex justify-content-between align-items-center">
      <h4 class="form-title">
        {{ matricula.id ? "Editar Aula Avulsa nº" : "Nova Aula Avulsa" }}
        <span v-if="matricula.id">
          {{ matricula.id }}
        </span>
      </h4>
      <button class="btn btn-secondary" @click="cancelar">Voltar</button>
    </div>

    <div class="spacer"></div>

    <!-- Seção 1: Dados do Contratante -->
    <div class="form-section">
      <h5 class="section-title">Dados do Contratante</h5>
      <div class="row">
        <!-- Campo Aluno -->
        <div class="col-md-6">
          <div class="form-group">
            <label for="aluno">Aluno <span class="required">*</span></label>
            <div class="d-flex">
              <input
                id="aluno"
                type="text"
                class="form-control pessoa-input disabled-input"
                :value="matricula.aluno_nome || ''"
                disabled
              />
              <button
                class="btn btn-primary pessoa-icon-btn"
                type="button"
                @click="abrirModalPessoa('aluno')"
              >
                <i class="bi bi-person"></i>
              </button>
            </div>
          </div>
        </div>

      <!-- Campo Responsável -->
      <div class="col-md-6">
        <div class="form-group">
          <label for="responsavel">
            Responsável
            <span v-if="responsavelObrigatorio" class="text-danger">*</span>
          </label>

          <div class="d-flex">
            <input
              id="responsavel"
              type="text"
              class="form-control pessoa-input disabled-input"
              :value="matricula.responsavel_nome || ''"
              disabled
            />

            <!-- ✅ Agora aparece também no Editar, quando for obrigatório -->
            <button
              v-if="responsavelObrigatorio"
              class="btn btn-primary pessoa-icon-btn"
              type="button"
              @click="abrirModalPessoa('responsavel')"
            >
              <i class="bi bi-person"></i>
            </button>
          </div>

          <!-- ✅ Aviso quando obrigatório e ainda não selecionado -->
          <small v-if="mostrarAvisoResponsavel" class="text-danger">
            Não há matrícula vinculada ao aluno (ou não foi possível localizar o responsável). A seleção do Responsável é obrigatória.
          </small>
        </div>
      </div>

      </div>
      <!-- Campo Observações -->
      <div class="row mt-3">
        <div class="col-md-12">
          <div class="form-group">
            <label for="observacoes_contratante">Observações</label>
            <textarea
              id="observacoes_contratante"
              v-model="matricula.observacoes_contratante"
              class="form-control"
              rows="3"
            ></textarea>
          </div>
        </div>
      </div>
    </div>

    <!-- Seção 2: Seleção de Nível e Turmas Avulsas -->
    <div class="form-section">
      <h5 class="section-title">Turmas Avulsas</h5>
      <div class="row">
        <!-- Campo de seleção de Nível -->
        <div class="col-md-4">
          <div class="form-group">
            <label for="nivel">Nível <span class="required">*</span></label>
            <select
              id="nivel"
              v-model="matricula.nivel_id"
              class="form-control"
              @change="onNivelChange"
            >
              <option value="">Selecione</option>
              <option v-for="nivel in niveis" :key="nivel.id" :value="nivel.id">
                {{ nivel.nivel }}
              </option>
            </select>
          </div>
        </div>
      </div>
      <!-- Tabela de Turmas -->
      <div class="form-group">
        <div class="table-responsive mt-2">
          <table class="table table-borderless">
            <thead>
              <tr>
                <th>Turma <span class="required">*</span></th>
                <th>Data da aula <span class="required">*</span></th>
                <th style="width: 50px;"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(turmaSelecionada, index) in matricula.turmas" :key="turmaSelecionada.linha_uid">
                <td>
                  <select
                    v-model="turmaSelecionada.turma_id"
                    class="form-control"
                    @change="handleTurmaChange(index, $event)"
                  >
                    <option value="" disabled>Selecione</option>
                    <option v-if="turmaSelecionada.turma_id && !turmaEstaDisponivel(turmaSelecionada)" :value="turmaSelecionada.turma_id" disabled>
                      {{ turmaSelecionada.turma_completa || turmaSelecionada.turma_id }} — {{ turmaHistoricaPreservada(turmaSelecionada) ? 'Atual / indisponível para novas escolhas' : 'Indisponível — selecione outra turma' }}
                    </option>
                    <option
                      v-for="turma in turmasDisponiveis"
                      :key="`${turma.id}|||${turma.dia}|||${turma.horario}`"
                      :value="`${turma.id}|||${turma.dia}|||${turma.horario}`"
                    >
                      {{ `Turma: ${turma.id} | Dia: ${turma.dia} | Hora: ${String(turma.horario || '').slice(0,5)} | Instrutor: ${turma.instrutor}` }}
                    </option>
                  </select>
                </td>
                <td>
                  <flat-pickr
                    v-model="turmaSelecionada.data_aula"
                    :config="getDatePickerConfig(turmaSelecionada)"
                    :key="`fp-${turmaSelecionada.linha_uid}`"
                    class="form-control">
                  </flat-pickr>
                </td>
                <td class="text-center">
                  <button @click="removerTurma(index)" class="btn btn-danger btn-sm">
                    🗑️
                  </button>
                </td>
              </tr>
              <tr v-if="matricula.turmas.length === 0">
                <td colspan="3" class="text-center">Nenhuma turma cadastrada.</td>
              </tr>
            </tbody>
          </table>
          <div class="add-turma-container">
            <button class="btn btn-primary" @click="adicionarTurmaSlot">
              <i class="bi bi-plus"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Seção 3: Dados Financeiros -->
    <div class="form-section">
      <h5 class="section-title">Dados Financeiros</h5>
      <div class="row mb-3">
        <!-- Valor Unitário -->
        <div class="col-md-3">
          <div class="form-group">
            <label for="valor_unitario">Valor Unitário</label>
            <input
              type="number"
              step="0.01"
              id="valor_unitario"
              class="form-control"
              v-model.number="matricula.valor_unitario"
            />
          </div>
        </div>
        <!-- Desconto -->
        <div class="col-md-2">
          <div class="form-group">
            <label for="desconto">Desconto (%)</label>
            <input
              type="number"
              id="desconto"
              v-model="matricula.desconto"
              class="form-control"
              min="0"
              max="100"
            />
          </div>
        </div>
        <!-- Valor com Desconto -->
        <div class="col-md-3">
          <div class="form-group">
            <label for="valor_com_desconto">Valor com Desconto</label>
            <input
              type="text"
              id="valor_com_desconto"
              class="form-control"
              :value="globalUtils.formatarValor(matricula.valor_com_desconto)"
              disabled
            />
          </div>
        </div>
        <!-- Qtd. Pagamentos -->
        <div class="col-md-2">
          <div class="form-group">
            <label for="qtd_pagamentos">Qtd. Pagamentos</label>
            <input
              type="text"
              id="qtd_pagamentos"
              class="form-control"
              :value="qtdPagamentos"
              disabled
            />
          </div>
        </div>
        <!-- Total a Pagar -->
        <div class="col-md-2">
          <div class="form-group">
            <label for="total_pagar">Total a Pagar</label>
            <input
              type="text"
              id="total_pagar"
              class="form-control"
              :value="globalUtils.formatarValor(totalAPagarEfetivo)"
              disabled
            />
          </div>
        </div>
      </div>

      <!-- Autorizações do Cartão (Aula Avulsa) -->
      <div class="row mt-2" v-if="temCartao">
        <div class="col-md-3" v-if="temCartaoCredito">
          <div class="form-group">
            <label>Autorização (Crédito) <span class="required">*</span></label>
            <input class="form-control" v-model="autorizacaoCredito" placeholder="Autorização do crédito" />
          </div>
        </div>
        <div class="col-md-3" v-if="temCartaoDebito">
          <div class="form-group">
            <label>Autorização (Débito) <span class="required">*</span></label>
            <input class="form-control" v-model="autorizacaoDebito" placeholder="Autorização do débito" />
          </div>
        </div>
      </div>

      <!-- Tabela de Pagamentos -->
      <div class="table-responsive mt-3">
        <table class="table table-borderless">
          <thead>
            <tr>
              <th>#</th>
              <th>Forma de Pagamento <span class="required">*</span></th>
              <th>Vencimento <span class="required">*</span></th>
              <th>Valor</th>
              <th>Pagamento <span class="required">*</span></th>
              <th>Recebimento em</th>
              <th v-if="existeCheque()">Banco nº</th>
              <th v-if="existeCheque()">Agência</th>
              <th v-if="existeCheque()">Cheque nº</th>
              <th>Conta Financeira <span class="required">*</span></th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(pagamento, index) in pagamentosOrdenados" :key="pagamento.id || index">
              <td>{{ index + 1 }}</td>
              <td>
                <select
                  v-model="pagamento.forma_pagamento_id"
                  class="form-control"
                  required
                >
                  <option value="">Selecione</option>
                  <option v-for="forma in formasPagamento" :key="forma.id" :value="forma.id">
                    {{ forma.nome }}
                  </option>
                </select>
              </td>
              <td>
                <input
                  v-model="pagamento.data_vencimento"
                  type="date"
                  class="form-control"
                  required
                />
              </td>
              <td>
                <input
                  :value="globalUtils.formatarValor(pagamento.valor)"
                  type="text"
                  class="form-control"
                  readonly
                />
              </td>
              <td>
                <input
                  v-model="pagamento.data_pagamento"
                  type="date"
                  class="form-control"
                  :disabled="isAInformar(pagamento.forma_pagamento_id)"
                />
              </td>

              <td>
                <input
                  v-model="pagamento.data_recebimento"
                  type="date"
                  class="form-control"
                  disabled
                />
              </td>

              <td v-if="isCheque(pagamento.forma_pagamento_id)">
                <input
                  v-model="pagamento.banco_numero"
                  type="text"
                  class="form-control"
                  placeholder="Banco nº"
                />
              </td>
              <td v-if="isCheque(pagamento.forma_pagamento_id)">
                <input
                  v-model="pagamento.agencia"
                  type="text"
                  class="form-control"
                  placeholder="Agência"
                />
              </td>
              <td v-if="isCheque(pagamento.forma_pagamento_id)">
                <input
                  v-model="pagamento.cheque_numero"
                  type="text"
                  class="form-control"
                  placeholder="Cheque nº"
                />
              </td>
              
              <td>
                <select
                  v-model.number="pagamento.conta_financeira_id"
                  class="form-control"
                  :disabled="isAInformar(pagamento.forma_pagamento_id) || contaOperacionalBloqueada(pagamento)"
                  :required="!isAInformar(pagamento.forma_pagamento_id) && !!(pagamento.data_pagamento && String(pagamento.data_pagamento).trim())"
                >
                  <!-- A informar -->
                  <option v-if="isAInformar(pagamento.forma_pagamento_id)" value="">A informar</option>

                  <!-- Demais -->
                  <template v-else>
                    <option :value="''" disabled>Selecione</option>
                    <option v-for="conta in contasFinanceiras" :key="conta.id" :value="Number(conta.id)">
                      {{ conta.apelido }}
                    </option>
                  </template>
                </select>
              </td>

              <td class="text-center">
                <button @click="removerPagamento(index)" class="btn btn-danger btn-sm">
                  🗑️
                </button>
              </td>
            </tr>
            <tr v-if="matricula.pagamentos.length === 0">
              <td colspan="10" class="text-center">Nenhum pagamento cadastrado.</td>
            </tr>
          </tbody>
        </table>
        <div class="d-flex justify-content-end mt-2">
          <button class="btn btn-primary ms-2" @click="adicionarPagamento">
            <i class="bi bi-plus"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- Botões de Ação -->
    <div class="form-actions d-flex justify-content-end">
      <button
        class="btn me-2"
        :class="isSubmitting ? 'btn-success' : 'btn-primary'"
        :disabled="!formValid || isSubmitting"
        @click="salvarAulaAvulsa"
      >
        <i :class="isSubmitting ? 'bi bi-hourglass-split me-1' : 'bi bi-save me-1'"></i>
        {{ isSubmitting ? 'Salvando…' : 'Salvar' }}
      </button>
      <button
        class="btn btn-secondary"
        :disabled="isSubmitting"
        @click="cancelar"
      >
        <i class="bi bi-x-circle me-1"></i>Cancelar
      </button>
    </div>

    <!-- Modal para seleção de pessoa -->
    <CarregarPessoaFisica
      :mostrar="modalPessoaAberto"
      @fechar="fecharModalPessoa"
      @selecionar="selecionarPessoa"
    />
  </div>
</template>

<script>
import { paymentCollectionOperations } from "@/utils/operationalPaymentPayload";
import { turmaDisponivel, mensagemTurmaIndisponivel } from '@/utils/turmasSelecao';
import { reactive, ref, onMounted, computed, watch, nextTick, getCurrentInstance } from "vue";
import { useRouter, useRoute } from "vue-router";
import axios from "@/services/axios";
import { loadOperationalAccounts, accountSelectionError, emptyAccountsMessage } from '@/services/operationalAccounts';
import globalUtils from "@/utils/globalUtils.js";
import CarregarPessoaFisica from "@/components/modals/CarregarPessoaFisica.vue";
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import { Portuguese } from "flatpickr/dist/l10n/pt.js";

const skipFormaWatcher = ref(false);

export default {
  name: "FormularioAulaAvulsa",
  components: { CarregarPessoaFisica, flatPickr },
  setup() {
    let originalPayments = [];
    const contaOperacionalBloqueada = payment => {
      const original = originalPayments.find(row => Number(row.id) === Number(payment.id));
      return !!original && !!(original.data_pagamento || original.data_recebimento || original.pago ||
        (original.cheque_status && original.cheque_status !== 'NORMAL'));
    };
    const router = useRouter();
    const route = useRoute();
        
    const { appContext } = getCurrentInstance();
    const userName = appContext.config.globalProperties.$globalState.userName || null;

    const isSubmitting = ref(false);

    const contasFinanceiras = ref([]);
    const contasMensagem = ref('');

    const autorizacaoCredito = ref('');
    const autorizacaoDebito = ref('');

    const temCartaoCredito = computed(() =>
      (matricula.pagamentos || []).some(p => isCartaoCredito(p.forma_pagamento_id))
    );
    const temCartaoDebito = computed(() =>
      (matricula.pagamentos || []).some(p => isCartaoDebito(p.forma_pagamento_id))
    );
    const temCartao = computed(() => temCartaoCredito.value || temCartaoDebito.value);

  
    const carregarContasFinanceiras = async () => {
      contasFinanceiras.value = [];
      contasMensagem.value = '';
      try {
        contasFinanceiras.value = await loadOperationalAccounts(route.params.id ? 'aula_avulsa_recebimento' : 'aula_avulsa_inclusao');
        if (!contasFinanceiras.value.length) contasMensagem.value = emptyAccountsMessage;
      } catch (err) {
        contasMensagem.value = accountSelectionError(err);
      }
    };

    const makeRowKey = () =>
      (crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`);


    const norm = s => (s ?? '')
      .normalize('NFD').replace(/[\u0300-\u036f]/g,'')
      .trim().toLowerCase();

    const contaPadraoGlobalId = computed(() => {
      const c = (contasFinanceiras.value || []).find(x => x.conta_padrao === true || x.conta_padrao === 1);
      return c?.id ? Number(c.id) : null;
    });

    const idBrinde = computed(() =>
      formasPagamento.value.find(fp => norm(fp.nome) === 'brinde')?.id ?? null
    );

    const hasBrinde = computed(() =>
      idBrinde.value != null &&                      // 👈 só considera se existir forma 'Brinde'
      Array.isArray(matricula.pagamentos) &&
      matricula.pagamentos.some(
        p => p.forma_pagamento_id === idBrinde.value
      )
    );

    // Dados da matrícula
    const matricula = reactive({
      responsavel_id: null,
      aluno_id: null,
      aluno_nome: "",
      responsavel_nome: "",
      observacoes_contratante: "",
      nivel_id: "",
      turmas: [], // Cada item: { turma_id, data_aula }
      valor_unitario: "0.00",
      desconto: 0,
      valor_com_desconto: "0.00",
      pagamentos: [] // Array de pagamentos
    });

    // Modal de pessoa
    const modalPessoaAberto = ref(false);
    const tipoPessoaSelecionada = ref(null);
    const abrirModalPessoa = (tipo) => {
      tipoPessoaSelecionada.value = tipo;
      modalPessoaAberto.value = true;
    };
    const fecharModalPessoa = () => {
      modalPessoaAberto.value = false;
      tipoPessoaSelecionada.value = null;
    };
    const selecionarPessoa = async ({ pessoa }) => {
      if (tipoPessoaSelecionada.value === "aluno") {
        matricula.aluno_nome = pessoa.nome;
        matricula.aluno_id   = pessoa.id;
        await verificarVinculoAluno(pessoa.id);  // 👈 define obrigatório/não obrigatório e preenche/limpa responsável
      } else if (tipoPessoaSelecionada.value === "responsavel") {
        matricula.responsavel_nome = pessoa.nome;
        matricula.responsavel_id   = pessoa.id;
      }
      fecharModalPessoa();
    };

    // Carregar níveis via API
    const niveis = ref([]);
    const carregarNiveis = async () => {
      try {
        const { data } = await axios.get('/cadastros/niveis-turmas', {
          params: { page: 1, limit: 10000 }
        });

        const lista = data?.data ?? data ?? [];
        const somenteAtivos = Array.isArray(lista)
          ? lista.filter(n => n.ativo !== false) // mantém se ativo=true ou undefined
          : [];

        // Ordenar por nome/nível (ajuste a chave se necessário)
        // somenteAtivos.sort((a, b) =>
        //   String(a.nivel ?? a.nome ?? '')
        //     .localeCompare(String(b.nivel ?? b.nome ?? ''), 'pt-BR', {
        //       sensitivity: 'base',
        //       numeric: true
        //     })
        // );

        niveis.value = somenteAtivos;
      } catch (error) {
        console.error('Erro ao carregar níveis:', error);
        niveis.value = [];
      }
    };

    // Carregar turmas disponíveis com base no nível selecionado
    const turmasDisponiveis = ref([]);

    const idxDia = (d) => {
      let s = String(d || '')
        .normalize('NFD').replace(/\p{Diacritic}/gu, '')
        .toLowerCase().replace(/-?feira/g, '').trim();
      s = s.split(/\s+/)[0].slice(0,3); // seg, ter, qua, qui, sex, sab, dom
      const mapa = { seg:1, ter:2, qua:3, qui:4, sex:5, sab:6, dom:7 };
      return mapa[s] ?? 99;
    };

    const toMin = (h) => {
      const m = String(h || '').match(/^(\d{1,2}):(\d{2})(?::\d{2})?/);
      if (!m) return Number.MAX_SAFE_INTEGER;
      return (+m[1]) * 60 + (+m[2]);
    };

    const todayLocalISO = () => {
      const d = new Date();
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    };

    const turmasOriginais = ref([]);
    const turmaEstaDisponivel = t => turmaDisponivel(t.turma_id, turmasDisponiveis.value, '|||');
    const turmaHistoricaPreservada = t => turmasOriginais.value.some(o => o.turma_id === t.turma_id && o.data_aula === t.data_aula && Number(o.nivel_id) === Number(matricula.nivel_id));
    let versaoTurmas = 0;
    const carregarTurmas = async () => {
      const versao = ++versaoTurmas;
      turmasDisponiveis.value = [];
      if (!matricula.nivel_id) {
        turmasDisponiveis.value = [];
        return;
      }
      try {
        // só dias/horários — já incluem instrutor_nome
        const res = await axios.get("/cadastros/dias-horarios-turmas", { params: { ativo: true } });
        if (versao !== versaoTurmas) return;
        const dias = res.data.data || res.data || [];

        // nome do nível selecionado
        const nomeNivel = niveis.value.find(n => n.id == matricula.nivel_id)?.nivel;
        if (!nomeNivel) {
          turmasDisponiveis.value = [];
          return;
        }

        turmasDisponiveis.value = dias
          .filter(item => item.nivel_nome.toLowerCase() === nomeNivel.toLowerCase())
          .map(item => ({
            id:        item.turma_id,
            dia:       item.dia_semana,
            horario:   item.horario,
            instrutor: item.instrutor_nome || "Não informado",
            nivel:     item.nivel_nome
          }))
          .sort((a, b) =>
            (Number(a.id) - Number(b.id)) ||          // 1) ID da turma (crescente)
            (idxDia(a.dia) - idxDia(b.dia)) ||        // 2) Dia (Seg→Dom)
            (toMin(a.horario) - toMin(b.horario))     // 3) Horário (crescente)
          );

        // garantir pelo menos uma linha
        if (!matricula.turmas.length) {
          matricula.turmas.push({ turma_id: "", data_aula: "" });
        }
      } catch (err) {
        console.error("❌ Erro ao carregar turmas:", err);
        turmasDisponiveis.value = [];
      }
    };

    // Chamado quando o nível é alterado
    const onNivelChange = async () => {
      await carregarTurmas();
    };

    // Adiciona uma nova linha para turma
    const adicionarTurmaSlot = () => {
      matricula.turmas.push({
        linha_uid: makeRowKey(),
        turma_id: "",
        data_aula: "",
        dia_semana: "",
        horario: "",
        turma_completa: ""
      });
    };

    // Atualiza a seleção da turma
    const atualizarTurma = (index) => {
      const turmaSelecionada = matricula.turmas[index];
      if (!turmaSelecionada.turma_id) {
        console.warn("Nenhuma turma selecionada.");
        return;
      }
    };

    // Remove uma linha da tabela de turmas
    const removerTurma = (index) => {
      matricula.turmas.splice(index, 1);
    };

    // Carregar valor da Aula Avulsa via API
    const carregarValorAulaAvulsa = async () => {
      try {
        const responseTipos = await axios.get("/financeiro/tipos-valores");
        const tiposValores = responseTipos.data.data || responseTipos.data;
        if (!Array.isArray(tiposValores)) {
          console.error("Estrutura inesperada para tipos_valores:", responseTipos.data);
          return;
        }
        const tipoAula = tiposValores.find(
          t => t.nome.toLowerCase().trim() === "aula avulsa"
        );
        if (!tipoAula) {
          console.warn("Tipo 'Aula Avulsa' não encontrado.");
          return;
        }
        const responseValores = await axios.get("/financeiro/valores");
        const valores = responseValores.data.data || responseValores.data;
        if (!Array.isArray(valores)) {
          console.error("Estrutura inesperada para valores:", responseValores.data);
          return;
        }
        const valorAula = valores.find((v) => v.tipo_id === tipoAula.id);
        if (!valorAula) {
          console.warn("Nenhum valor encontrado para o tipo 'Aula Avulsa'.");
          return;
        }
        if (!matricula.id) {
          matricula.valor_unitario = parseFloat(valorAula.valor).toFixed(2);
          calcularValorComDescontoAulaAvulsa();
        }
      } catch (error) {
        console.error("Erro ao carregar valor da aula avulsa:", error);
      }
    };

    // Calcular Valor com Desconto
    const calcularValorComDescontoAulaAvulsa = () => {
      const valorBase = parseFloat(matricula.valor_unitario) || 0;
      const desconto = parseFloat(matricula.desconto) || 0;
      matricula.valor_com_desconto = (valorBase - (valorBase * desconto) / 100).toFixed(2);
    };

    // Watch para recalcular automaticamente quando valor_unitario ou desconto mudarem
    watch(
      () => [matricula.valor_unitario, matricula.desconto],
      () => {
        calcularValorComDescontoAulaAvulsa();
      }
    );

    const isEditMode = computed(() => Boolean(route.params.id));

    const alunoTemMatricula = ref(null); // null antes de verificar, true/false depois
    const responsavelObrigatorio = computed(() => alunoTemMatricula.value === false);
    // const responsavelDisabled = computed(() => isEditMode.value || !responsavelObrigatorio.value);

    const mostrarAvisoResponsavel = computed(
      () => responsavelObrigatorio.value && !matricula.responsavel_id
    );


    // Computed: Quantidade de pagamentos (baseada no array de pagamentos)
    const qtdPagamentos = computed(() => matricula.pagamentos.length);
    // Computed: Total a Pagar = Valor com Desconto * quantidade de turmas contratadas

    const totalAPagar = computed(() => {
      const valor = parseFloat(matricula.valor_com_desconto) || 0;
      const quantidade = matricula.turmas.length || 1; // assume 1 aula enquanto não tiver nenhuma
      return (valor * quantidade).toFixed(2);
    });

    // total efetivo: zera se há brinde
    const totalAPagarEfetivo = computed(() =>
      hasBrinde.value ? '0.00' : totalAPagar.value
    );

    // Função para recalcular o valor de cada parcela não paga
    const recalcularParcelas = () => {
      if (!Array.isArray(matricula.pagamentos) || matricula.pagamentos.length === 0) return;

      // Quando houver Brinde, o total efetivo é 0
      const total = parseFloat(totalAPagarEfetivo.value) || 0;

      const ehPago = (p) => {
        const formaId = p.forma_pagamento_id;
        const temData = Boolean(p.data_pagamento && String(p.data_pagamento).trim());
        const temConta = !!p.conta_financeira_id;
        const valorPositivo = Number(p.valor) > 0;

        if (isAInformar(formaId)) return false;
        if (isEspecie(formaId))   return temData && valorPositivo;
        return temData && temConta && valorPositivo;
      };

      const ehNaoPago = (p) => !ehPago(p);

      // Zera imediatamente os brindes e não contam como pagos/não pagos para rateio
      const brindes = idBrinde.value
        ? matricula.pagamentos.filter(p => p.forma_pagamento_id === idBrinde.value)
        : [];

      brindes.forEach(p => { p.valor = '0.00'; });

      const restantes = idBrinde.value
        ? matricula.pagamentos.filter(p => p.forma_pagamento_id !== idBrinde.value)
        : matricula.pagamentos;

      const pagos    = restantes.filter(ehPago);
      const naoPagos = restantes.filter(ehNaoPago);

      const totalPago = pagos.reduce((acc, p) => acc + (Number.isFinite(+p.valor) ? +p.valor : 0), 0);
      const restante = Math.max(total - totalPago, 0);

      const n = naoPagos.length;
      if (n === 0) return;

      const padrao = Number((restante / n).toFixed(2));
      const somaPadrao = padrao * (n - 1);
      const ultimo = Number((restante - somaPadrao).toFixed(2));

      let i = 0;
      matricula.pagamentos.forEach(p => {
        if (p.forma_pagamento_id === idBrinde.value) {
          p.valor = '0.00'; // mantém 0
        } else if (ehNaoPago(p)) {
          p.valor = (i === n - 1 ? ultimo : padrao).toFixed(2);
          i++;
        }
        // pagos permanecem com o valor já informado
      });
    };

    // Watch para recalcular parcelas sempre que o total a pagar mudar
    watch(totalAPagar, () => {
      recalcularParcelas();
    });

    // Funções para pagamentos
    const adicionarPagamento = () => {
      const hoje = todayLocalISO();
      matricula.pagamentos.push({
        forma_pagamento_id: null,
        data_vencimento: hoje,
        data_pagamento: hoje,   // watchers ajustam ao trocar forma
        data_recebimento: null,
        valor: '',
        banco_numero: '',
        agencia: '',
        cheque_numero: '',
        conta_financeira_id: null,
      });
      recalcularParcelas();
    };

    const removerPagamento = (index) => {
      matricula.pagamentos.splice(index, 1);
      recalcularParcelas();
    };

    const verificarVinculoAluno = async (alunoId, { preservarResponsavelSeExistente = false } = {}) => {
      try {
        const { data } = await axios.get('/operacional/aulas-avulsas/responsavel', {
          params: { aluno_id: alunoId }
        });

        const temMatricula = !!data?.tem_matricula;
        const respId = data?.responsavel_id ?? null;
        const respNome = data?.responsavel_nome ?? '';

        // ✅ Se disse que tem matrícula, mas NÃO retornou responsável, trata como "sem responsável"
        // (para cair na regra de obrigatoriedade)
        const temResponsavelValido = !!respId;

        alunoTemMatricula.value = temMatricula && temResponsavelValido;

        if (alunoTemMatricula.value) {
          // tem matrícula e responsável → autopreenche
          matricula.responsavel_id = respId;
          matricula.responsavel_nome = respNome;
        } else {
          // não tem matrícula OU não tem responsável associado → obrigatório selecionar manualmente
          if (!(preservarResponsavelSeExistente && matricula.responsavel_id)) {
            matricula.responsavel_id = null;
            matricula.responsavel_nome = '';
          }
        }
      } catch (err) {
        console.error('Erro ao verificar vínculo do aluno:', err);

        // Em caso de erro, por segurança: exigir responsável
        alunoTemMatricula.value = false;

        if (!(preservarResponsavelSeExistente && matricula.responsavel_id)) {
          matricula.responsavel_id = null;
          matricula.responsavel_nome = '';
        }
      }
    };

    // Carregar formas de pagamento via API
    const formasPagamento = ref([]);
    const carregarFormasPagamento = async () => {
      try {
        const { data } = await axios.get('/cadastros/formas-pagamento');

        const norm = s => (s ?? '')
          .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
          .trim().toLowerCase();

        const lista = Array.isArray(data?.data) ? data.data : (Array.isArray(data) ? data : []);

        const ordemPersonalizada = {
          'pix': 1,
          'especie': 2,
          'cartao de credito a vista': 3,
          'cartao de credito parcelado': 4,
          'cartao de debito': 5,
          'boleto': 6,
          'transferencia bancaria': 7,
          'cheque': 8,
          'a informar': 9,
        };

        formasPagamento.value = lista
          .sort((a, b) => {
            const oa = ordemPersonalizada[norm(a.nome)] ?? 999;
            const ob = ordemPersonalizada[norm(b.nome)] ?? 999;
            if (oa !== ob) return oa - ob;
            return a.nome.localeCompare(b.nome, 'pt-BR');
          });

      } catch (error) {
        console.error("Erro ao carregar formas de pagamento:", error);
      }
    };


    // Função para verificar se a forma de pagamento é Cheque
    const isCheque = (formaPagamentoId) => {
      const forma = formasPagamento.value.find((fp) => fp.id === formaPagamentoId);
      return forma ? forma.nome.toLowerCase() === "cheque" : false;
    };

    // Verifica se existe algum pagamento com cheque (para exibir colunas)
    const existeCheque = () => {
      return matricula.pagamentos.some((pagamento) => isCheque(pagamento.forma_pagamento_id));
    };

    const isEspecie = (formaPagamentoId) => {
      const forma = formasPagamento.value.find(f => f.id === formaPagamentoId)
      return forma?.nome.toLowerCase() === 'espécie'
    }
    const isAInformar = (formaPagamentoId) => {
      const forma = formasPagamento.value.find(f => f.id === formaPagamentoId)
      return forma?.nome.toLowerCase().includes('informar')
    }

    const getFormaById = (id) => formasPagamento.value.find(f => Number(f.id) === Number(id));

    const isCartao = (formaId) => {
      const n = norm(getFormaById(formaId)?.nome);
      return n.includes('cartao');
    };
    const isCartaoCredito = (formaId) => {
      const n = norm(getFormaById(formaId)?.nome);
      return n.includes('cartao') && n.includes('credito');
    };
    const isCartaoDebito = (formaId) => {
      const n = norm(getFormaById(formaId)?.nome);
      return n.includes('cartao') && n.includes('debito');
    };

    // ✅ aplica conta padrão por forma -> fallback global (exceto espécie/a informar)
    const aplicarContaPadraoSeVazio = (formaId, targetObj, fieldName = 'conta_financeira_id') => {
      const forma = getFormaById(formaId);
      if (!forma || !targetObj) return;

      const nome = norm(forma.nome);
      if (nome === 'especie' || nome === 'a informar' || nome === 'informar') return;

      const atual = targetObj[fieldName];
      if (atual !== null && atual !== '' && atual !== undefined) return;

      const porForma = forma.conta_financeira_padrao_id ? Number(forma.conta_financeira_padrao_id) : null;
      if (porForma) {
        targetObj[fieldName] = porForma;
        return;
      }

      const fallback = contaPadraoGlobalId.value;
      if (fallback) targetObj[fieldName] = fallback;
    };

        // Função computada para validar o formulário
    const formValid = computed(() => {
      // Dados do contratante
      if (!matricula.aluno_id) return false;
      if (responsavelObrigatorio.value && !matricula.responsavel_id) return false;
      // Nível
      if (!matricula.nivel_id) return false;
      // Turmas: pelo menos uma e cada uma com turma_id e data_aula preenchidos
      if (!matricula.turmas.length) return false;
      for (const turma of matricula.turmas) {
        if (!turma.turma_id || !turma.data_aula) return false;
      }
      // Pagamentos: pelo menos um e cada um com forma_pagamento_id e data_vencimento preenchidos
      if (!matricula.pagamentos.length) return false;
      for (const pag of matricula.pagamentos) {
        const formaId = pag.forma_pagamento_id;
        // sempre exige forma e vencimento
        if (!formaId || !pag.data_vencimento) return false;

        // A informar → ignora pagamento e conta
        if (isAInformar(formaId)) {
          continue;
        }

        // Se informou pagamento → exige conta (inclui Espécie)
        if ((pag.data_pagamento || '').trim() && !pag.conta_financeira_id) {
          return false;
        }

        // Cheque com pagamento informado → exige dados do cheque
        if (isCheque(formaId) && (pag.data_pagamento || '').trim()) {
          if (!pag.banco_numero || !pag.agencia || !pag.cheque_numero) return false;
        }

        // Cartão exige autorização por tipo (e brinde não valida)
        if (idBrinde.value != null && pag.forma_pagamento_id === idBrinde.value) {
          continue;
        }

        if (isCartao(formaId)) {
          if (isCartaoCredito(formaId) && !(autorizacaoCredito.value || '').trim()) return false;
          if (isCartaoDebito(formaId)  && !(autorizacaoDebito.value  || '').trim()) return false;
        }
      }
      return true;
    });

    function formatDateForInput(dateString) {
      if (!dateString) return "";
      // Se vier algo como "2025-04-18T15:30:00Z" ou com qualquer fuso,
      // aqui pegamos apenas "2025-04-18"
      return dateString.split("T")[0];
    }

    // Função para carregar os dados de uma aula avulsa para edição
    const carregarAulaAvulsa = async (id) => {
      try {
        skipFormaWatcher.value = true;

        await carregarContasFinanceiras();  

        const aulaResponse = await axios.get(`/operacional/aulas-avulsas/${id}`);
        const aulaData = aulaResponse.data;

        matricula.id = aulaData.id;
        matricula.responsavel_id = aulaData.responsavel_id;
        matricula.aluno_id = aulaData.aluno_id;
        matricula.aluno_nome = aulaData.aluno_nome;
        matricula.responsavel_nome = aulaData.responsavel_nome;
        matricula.desconto = aulaData.desconto;
        matricula.observacoes_contratante = aulaData.observacoes;
        matricula.valor_unitario = aulaData.valor.toString();

        // Carrega as turmas contratadas
        const contratadasResponse = await axios.get(`/operacional/aulas-avulsas-contratadas/${id}`);
        matricula.turmas = contratadasResponse.data.map(item => ({
          nivel_id: item.nivel_id,
          linha_uid: item.id ? `ctr-${item.id}` : makeRowKey(),
          turma_id: `${item.turma_id}|||${item.dia_semana}|||${item.horario}`,
          data_aula: formatDateForInput(item.data_aula),
          horario: item.horario,
          dia_semana: item.dia_semana,
          turma_completa: item.turma_completa
        }));

        turmasOriginais.value = matricula.turmas.map(t => ({ ...t }));

        // Atribui o nivel_id
        if (aulaData.nivel_id) {
          matricula.nivel_id = aulaData.nivel_id;
        } else if (contratadasResponse.data.length > 0) {
          matricula.nivel_id = contratadasResponse.data[0].nivel_id;
        }

        // Após definir o nível, carrega as opções de turmas disponíveis
        await carregarTurmas();

        // Carrega os pagamentos
        const pagamentosResponse = await axios.get(`/operacional/aulas-avulsas-pagamentos/${id}`);
        originalPayments = pagamentosResponse.data || [];

        // Garante que matricula.pagamentos seja um array
        matricula.pagamentos = Array.isArray(pagamentosResponse.data)
          ? pagamentosResponse.data.map(p => {
              const mapped = {
                id: p.id,
                data_recebimento: p.data_recebimento ? formatDateForInput(p.data_recebimento) : "",
                forma_pagamento_id: p.forma_pagamento_id,
                data_vencimento: formatDateForInput(p.vencimento),
                valor: (Number(p.valor) || 0).toFixed(2),
                data_pagamento: p.data_pagamento ? formatDateForInput(p.data_pagamento) : "",
                banco_numero: p.banco_numero,
                agencia: p.agencia,
                cheque_numero: p.cheque_numero,
                conta_financeira_id: p.conta_financeira_id != null ? Number(p.conta_financeira_id) : null,
                numero_autorizacao: p.numero_autorizacao || null,
              };
              // Brinde → força 0,00
              if (mapped.forma_pagamento_id === idBrinde.value) mapped.valor = '0.00';
              return mapped;
            })
          : [];

          const firstCredito = matricula.pagamentos.find(p => isCartaoCredito(p.forma_pagamento_id) && (p.numero_autorizacao || '').trim());
          const firstDebito  = matricula.pagamentos.find(p => isCartaoDebito(p.forma_pagamento_id)  && (p.numero_autorizacao || '').trim());
          autorizacaoCredito.value = firstCredito?.numero_autorizacao?.trim?.() || '';
          autorizacaoDebito.value  = firstDebito?.numero_autorizacao?.trim?.()  || '';

        calcularValorComDescontoAulaAvulsa();
        recalcularParcelas();

        // garante que o DOM/computed estabilize antes de liberar o watcher
        await nextTick();
        skipFormaWatcher.value = false;


      } catch (error) {
        console.error("Erro ao carregar aula avulsa:", error);
        matricula.pagamentos = []; // Garante array vazio em caso de erro
        skipFormaWatcher.value = false;

      }
    };

    const toNull = v =>
      (v === undefined || v === null || (typeof v === 'string' && v.trim() === ''))
        ? null : v;

    const toNum = (v, def = 0) => {
      const n = Number(v);
      return Number.isFinite(n) ? n : def;
    };

    const nomeForma = (id) =>
      (formasPagamento.value.find(f => f.id === id)?.nome || '').toLowerCase();

    
    // Função de salvamento que monta os payloads e os envia
    const salvarAulaAvulsa = async () => {
      if (isSubmitting.value) return;
      if (matricula.turmas.some(t => !turmaEstaDisponivel(t) && !turmaHistoricaPreservada(t))) { alert(mensagemTurmaIndisponivel); return; }
      isSubmitting.value = true;

      try {
        // 1. Monta o payload para a tabela aulas_avulsas
        const payloadAulaAvulsa = {
          responsavel_id: matricula.responsavel_id,   // 👈 sem fallback
          aluno_id: matricula.aluno_id,
          desconto: parseFloat(matricula.desconto),
          quantidade_pagamentos: matricula.pagamentos.length,
          pagamento_pendente: matricula.pagamentos.some(p => !p.data_pagamento || p.data_pagamento.trim() === ""),
          observacoes: matricula.observacoes_contratante,
          valor: parseFloat(matricula.valor_unitario),
          quantidade_aulas: matricula.turmas.length,
          total_pagar: parseFloat(totalAPagarEfetivo.value)
        };

        // 2. Monta o payload para aulas_avulsas_contratadas
        const payloadAulasAvulsasContratadas = matricula.turmas.map(turmaItem => {
          // Agora, como o valor vem no formato "turmaId|||dia_semana|||horario"
          const parts = turmaItem.turma_id.split("|||");
          const turmaId = parts[0];
          // Use os valores já armazenados (se houver) ou os que vêm do lookup
          const horario = turmaItem.horario || parts[2] || "";
          const dia_semana = turmaItem.dia_semana || parts[1] || "";
          // Se houver um valor em turma_completa, usa-o; caso contrário, tenta montar a partir dos dados disponíveis
          let turma_completa = turmaItem.turma_completa;
          if (!turma_completa && turmaId && dia_semana && horario) {
            // Se possível, tente encontrar o instrutor na lista de turmas disponíveis
            const turmaDetalhe = turmasDisponiveis.value.find(t => t.id.toString() === turmaId);
            const instrutor = turmaDetalhe ? turmaDetalhe.instrutor : "";
            turma_completa = `Turma: ${turmaId} | Dia: ${dia_semana} | Hora: ${horario} | Instrutor: ${instrutor}`;
          }
          return {
            nivel_id: matricula.nivel_id,
            turma_id: parseInt(turmaId, 10),
            data_aula: turmaItem.data_aula,
            horario: horario,
            dia_semana: dia_semana,
            turma_completa: turma_completa || ""
          };
        });

        payloadAulaAvulsa.aulas = payloadAulasAvulsasContratadas;
        payloadAulaAvulsa.nome_usuario = userName;
        payloadAulaAvulsa.tem_brinde = hasBrinde.value;

        // 3. Monta o payload para aulas_avulsas_pagamentos
        const pagamentosSanitizados = matricula.pagamentos.map(p => {
          const nome = nomeForma(p.forma_pagamento_id);
          const ehInformar = nome.includes('informar');
          const ehCheque   = nome === 'cheque' || nome.includes('cheque');
          const pago = !!(p.data_pagamento && String(p.data_pagamento).trim());

          return {
            id: p.id,
            forma_pagamento_id: p.forma_pagamento_id,
            valor: toNum(p.valor, 0),
            vencimento: p.data_vencimento,                         // YYYY-MM-DD
            data_pagamento: ehInformar ? null : toNull(p.data_pagamento),
            data_recebimento: ehInformar ? null : toNull(p.data_recebimento),
            banco_numero:  ehCheque ? toNull(p.banco_numero)  : null,
            agencia:       ehCheque ? toNull(p.agencia)       : null,
            cheque_numero: ehCheque ? toNull(p.cheque_numero) : null,
            conta_financeira_id: (ehInformar || !pago) ? null : toNull(p.conta_financeira_id),
            pago,
            numero_autorizacao: (() => {
              if (!isCartao(p.forma_pagamento_id)) return null;
              if (isCartaoCredito(p.forma_pagamento_id)) return (autorizacaoCredito.value || '').trim() || null;
              if (isCartaoDebito(p.forma_pagamento_id))  return (autorizacaoDebito.value  || '').trim() || null;
              return (autorizacaoCredito.value || '').trim() || (autorizacaoDebito.value || '').trim() || null;
            })(),
          };
        });

        const operations = paymentCollectionOperations(route.params.id ? originalPayments : [], pagamentosSanitizados, 'vencimento', {operationalAccountSelection:true});
        const payload = { ...payloadAulaAvulsa, ...operations };
        if (route.params.id) {
          await axios.put(`/operacional/aulas-avulsas/${route.params.id}/salvar-completo`, payload);
        } else {
          await axios.post('/operacional/aulas-avulsas/salvar-completo', payload);
        }

        router.push("/operacional/aulas-avulsas");
      } catch (error) {
        console.error("Erro ao salvar aula avulsa:", error);
        alert(error.response?.data?.error || "Erro ao salvar aula avulsa. Verifique os logs para mais detalhes.");
      } finally {
        // libera o botão em qualquer cenário
        isSubmitting.value = false;
      }
    };

    const handleTurmaChange = (index, event) => {
      const selectedValue = event.target.value; // Ex: "7|||domingo|||08:00:00"
      // Atualiza o v-model com o valor completo para que a opção continue selecionada
      matricula.turmas[index].turma_id = selectedValue;
      const parts = selectedValue.split("|||");
      if (parts.length >= 3) {
        matricula.turmas[index].turmaId = parts[0]; // Guarda o ID numérico
        matricula.turmas[index].dia_semana = parts[1];
        matricula.turmas[index].horario = parts[2];
        // Busca na lista de turmas disponíveis para obter o instrutor
        const selectedTurma = turmasDisponiveis.value.find(t =>
          t.id.toString() === parts[0] &&
          t.dia.toLowerCase() === parts[1].toLowerCase() &&
          t.horario === parts[2]
        );
        const instrutor = selectedTurma ? selectedTurma.instrutor : "";
        matricula.turmas[index].turma_completa = `Turma: ${parts[0]} | Dia: ${parts[1]} | Hora: ${parts[2]} | Instrutor: ${instrutor}`;
      }
    };

    const convertDayNameToNumber = (dayName) => {
      const dias = {
        "domingo": 0,
        "segunda-feira": 1,
        "terça-feira": 2,
        "quarta-feira": 3,
        "quinta-feira": 4,
        "sexta-feira": 5,
        "sábado": 6
      };
      return dias[dayName.toLowerCase()] ?? null;
    };

    const getDatePickerConfig = (turma) => {
      const configBase = {
        dateFormat: "Y-m-d", // formato ISO para o v-model
        altInput: true,      // habilita o input alternativo
        altFormat: "d/m/Y",   // formato exibido para o usuário
        locale: Portuguese // Define o idioma para Português
      };

      if (!turma.dia_semana) return configBase;

      const diaDesejado = convertDayNameToNumber(turma.dia_semana);
      return {
        ...configBase,
        disable: [
          function(date) {
            return date.getDay() !== diaDesejado;
          }
        ]
      };
    };

    // Computed para retornar os pagamentos ordenados pelo vencimento (crescente)
    const pagamentosOrdenados = computed(() => {
      return [...matricula.pagamentos]
        .sort((a, b) => {
          const da = new Date(a.data_vencimento);
          const db = new Date(b.data_vencimento);
          return da - db;
        });
    });

    function calcularDataPagamento(vencimento, formaId) {
      if (!vencimento || !formaId) return "";
      const forma = formasPagamento.value.find(f => f.id === formaId);
      const nome  = (forma?.nome || "").toLowerCase();
      const dias  = nome.includes("crédito") ? 30
                  : nome.includes("débito")   ? 1
                  : (Number(forma?.prazo_pagamento) || 0);
      const d0 = new Date(`${vencimento}T00:00:00`);
      d0.setDate(d0.getDate() + dias);
      return globalUtils.nextBusinessDay(d0).toISOString().slice(0,10);
    }

    function calcularDataRecebimento(dataPagIso, formaId) {
      if (!dataPagIso || !formaId) return '';
      return globalUtils.calcularDataRecebimento(
        dataPagIso,
        formaId,
        formasPagamento.value
      ) || '';
    }

    watch(
      () => [autorizacaoCredito.value, autorizacaoDebito.value],
      ([autC, autD]) => {
        const credito = (autC || '').trim();
        const debito  = (autD || '').trim();

        (matricula.pagamentos || []).forEach((p) => {
          const formaId = p.forma_pagamento_id;
          if (!formaId) return;

          // só aplica para cartão (não mexe em espécie/a informar)
          if (!isCartao(formaId)) return;

          if (isCartaoCredito(formaId)) {
            p.numero_autorizacao = credito || null;
          } else if (isCartaoDebito(formaId)) {
            p.numero_autorizacao = debito || null;
          } else {
            // cartão genérico (se existir)
            p.numero_autorizacao = credito || debito || null;
          }
        });
      }
    );

    watch(
      () => matricula.pagamentos.map(p => p.forma_pagamento_id),
      (novasFormas, antigasFormas) => {
        if (skipFormaWatcher.value) return;

        // primeira execução: não mexe (evita quebrar hidratação/edição)
        if (!Array.isArray(antigasFormas)) return;

        novasFormas.forEach((novaForma, idx) => {
          const formaAntiga = antigasFormas[idx];
          if (novaForma == null) return;
          if (novaForma === formaAntiga) return; // não mudou

          const pag = matricula.pagamentos[idx];
          if (!pag) return;

          // ✅ ao mudar forma: zera conta e autorização da linha
          pag.conta_financeira_id = null;
          pag.numero_autorizacao = null;

          // ✅ A INFORMAR: tudo em aberto
          if (isAInformar(novaForma)) {
            pag.data_pagamento = null;
            pag.data_recebimento = null;
            // não preenche conta, não preenche autorização
            return;
          }

          // ✅ ESPÉCIE: zera conta e autorização (não aplica padrão)
          if (isEspecie(novaForma)) {
            // mantém recebimento calculado se houver data_pagamento
            if (pag.data_pagamento) {
              pag.data_recebimento = calcularDataRecebimento(pag.data_pagamento, novaForma) || null;
            } else {
              pag.data_recebimento = null;
            }
            return;
          }

          // ✅ DEMAIS FORMAS:
          // recalcula recebimento com base no pagamento atual (se houver)
          if (pag.data_pagamento) {
            pag.data_recebimento = calcularDataRecebimento(pag.data_pagamento, novaForma) || null;
          } else {
            pag.data_recebimento = null;
          }

          // ✅ aplica conta padrão automaticamente (por forma -> fallback global)
          aplicarContaPadraoSeVazio(novaForma, pag, 'conta_financeira_id');

          // ✅ cartão: define autorização conforme tipo (se os campos já estão preenchidos)
          if (isCartao(novaForma)) {
            const autC = (autorizacaoCredito.value || '').trim();
            const autD = (autorizacaoDebito.value || '').trim();

            if (isCartaoCredito(novaForma)) {
              pag.numero_autorizacao = autC || null;
            } else if (isCartaoDebito(novaForma)) {
              pag.numero_autorizacao = autD || null;
            } else {
              // cartão "genérico" (se existir)
              pag.numero_autorizacao = autC || autD || null;
            }
          }
        });

        // ✅ garante ajuste dos valores quando a forma muda
        recalcularParcelas();
      },
      { deep: true }
    );

    watch(totalAPagarEfetivo, () => { recalcularParcelas(); });

    watch(
      () => matricula.pagamentos.map(p => p.data_pagamento),
      (novasDatas) => {
        novasDatas.forEach((dataPag, idx) => {
          const pag = matricula.pagamentos[idx];
          const formaId = pag.forma_pagamento_id;

          if (!dataPag) {
            pag.data_recebimento = null;
            return;
          }

          // A informar → já está tratado no outro watch, mas reforça
          if (isAInformar(formaId)) {
            pag.data_recebimento = null;
            return;
          }

          // delega ao global (ele já trata espécie/pix/cheque/boleto = D+0, TED = dia útil, etc.)
          pag.data_recebimento = calcularDataRecebimento(dataPag, formaId) || null;
        });
      },
      { immediate: true }
    );

    // Botões de ação: voltar, cancelar e navegação
    const voltar = () => {
      router.push("/operacional/aulas-avulsas");
    };
    const cancelar = () => {
      router.push("/operacional/aulas-avulsas");
    };

    // Se estivermos em modo de edição, carrega os dados
    onMounted(async () => {
      await carregarNiveis();
      await carregarFormasPagamento();
      await carregarContasFinanceiras();

      if (route.params.id) {
        await carregarAulaAvulsa(route.params.id);      // já preenche responsavel_* a partir do back
        if (matricula.aluno_id) {
          // 👇 evita que a checagem zere o responsável em alunos sem matrícula
          await verificarVinculoAluno(matricula.aluno_id, { preservarResponsavelSeExistente: true });
        }
      } else {
        if (matricula.nivel_id) await carregarTurmas();
        await carregarValorAulaAvulsa();
        // 👇 cria o 1º pagamento já com hoje em Vencimento e Pagamento
      }
    });

    return {
      pagamentosOrdenados,
      contasFinanceiras,
      contasMensagem,
      contaOperacionalBloqueada,
      calcularDataPagamento,
      // atualizarParcela,
      handleTurmaChange,
      matricula,
      modalPessoaAberto,
      abrirModalPessoa,
      fecharModalPessoa,
      selecionarPessoa,
      niveis,
      turmasDisponiveis,
      onNivelChange,
      turmaEstaDisponivel,
      turmaHistoricaPreservada,
      adicionarTurmaSlot,
      atualizarTurma,
      removerTurma,
      calcularValorComDescontoAulaAvulsa,
      qtdPagamentos,
      totalAPagar,
      totalAPagarEfetivo,
      recalcularParcelas,
      adicionarPagamento,
      removerPagamento,
      formasPagamento,
      isCheque,
      isEspecie,
      isAInformar,
      existeCheque,
      salvarAulaAvulsa,
      voltar,
      cancelar,
      formValid,
      isSubmitting,
      globalUtils,
      getDatePickerConfig,
      convertDayNameToNumber,
      alunoTemMatricula,
      responsavelObrigatorio,
      // responsavelDisabled,
      mostrarAvisoResponsavel,
      verificarVinculoAluno,
      isEditMode,

      contaPadraoGlobalId,
      aplicarContaPadraoSeVazio,
      isCartao,
      isCartaoCredito,
      isCartaoDebito,
      temCartao,
      temCartaoCredito,
      temCartaoDebito,
      autorizacaoCredito,
      autorizacaoDebito,

    };
  }
};
</script>

<style scoped>

.flatpickr-calendar {
  font-family: 'Roboto', sans-serif; /* Fonte comum no Vuetify */
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  border-radius: 4px;
}

.flatpickr-day.selected {
  background-color: #1976d2; /* Cor primária do Vuetify */
  color: white;
}

.flatpickr-day:hover {
  background-color: #e0e0e0;
}

.form-container {
  margin: 20px;
}
.form-section {
  background: white;
  padding: 15px;
  border-radius: 8px;
  border: 1px solid #dee2e6;
  margin-bottom: 1rem;
}
.section-title {
  font-size: 0.9rem;
  font-weight: bold;
  margin-bottom: 10px;
}
.disabled-input {
  background: #e9ecef;
  border: 1px solid #ced4da;
}
.required {
  color: red;
}
.pessoa-input {
  flex: 1;
}
.add-turma-container {
  display: flex;
  justify-content: flex-end;
  margin-top: 5px;
}
.form-actions {
  margin-top: 20px;
}

.spacer {
  margin-bottom: 20px;
}


</style>
